﻿import { useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { QrCode, Edit, Trash, Inbox, Truck, FileText } from "lucide-react";
import printJS from "print-js";

import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import BasicConfirm from "@/components/Confirm/BasicConfirm";

import { hasPermission } from "@/services/Utils";
import { usePrintCodes } from "@/hooks/inventories/useInventories";
import { base64toBlob } from "@/hooks/printer/useBase64";

import { useOrderSalePaginate, useOrderSaleDelete, useOrderSaleReceive, useOrderSaleConfirmAssign } from "@/hooks/purchases/orders_sale";
import { SELECT_ORDER_DATA } from "@/redux/types/orders.types";
import OrdersSaleAssign from "../OrdersSaleAssign";
import { textConstants } from "@/languages/textConstants";

const OrdersSaleActionItem = ({ item, indice, data, base }) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();

	const { customer } = useSelector((state) => state.auth.user);

	const { mutate: remove, isLoading: loadDelete } = useOrderSaleDelete(base);
	const { mutate: receive, isLoading: loadReceive } = useOrderSaleReceive(base);
	const { mutate: assign, isLoading: loadAssign } = useOrderSaleConfirmAssign(base);
	const { mutate: print, isLoading: loadPrint } = usePrintCodes();

	const { data: response } = useOrderSalePaginate(base);
	const { permissions, permissions_delivery } = response ? response : { permissions: [] };

	const navigate = useNavigate();

	const [showDelete, setShowDelete] = useState(false);
	const [showReceive, setShowReceive] = useState(false);
	const [showDelivery, setShowDelivery] = useState(false);
	const [showAssign, setShowAssign] = useState(false);

	const handleEdit = () => {
		navigate(`/facturas/editar/${item.code}`);
	};

	const handleReceive = () => {
		setShowReceive(false);

		const request = {
			base,
			element: item,
		};

		receive(request, {
			onSuccess: (_response) => {
				addToast(`Factura recepcionada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handleDelivery = () => {
		setShowDelivery(false);

		let newDetails = item.items.filter((it) => Number(it.generic) !== 1);
		let newDetailsDelivery = [];

		newDetails.forEach((element, i) => {
			if (Number(newDetails[i].quantity) - Number(newDetails[i].quantity_delivery) > 0) {
				newDetails[i].id = "";
				newDetails[i].quantity = Number(newDetails[i].quantity) - Number(newDetails[i].quantity_delivery);
				newDetails[i].container_content = element.container_content;
				newDetails[i].unit_measurement = element.base_unit;
				newDetails[i].save_as = element.product_customer_id;
				newDetails[i].expiration_date = element.expiration_date ? element.expiration_date.substring(0, 10) : "";

				newDetailsDelivery.push(newDetails[i]);
			}
		});

		const newOrder = {
			...item,
			id: "",
			code: "",
			date_orders_delivery: "",
			delivery_number: "",
			items: newDetailsDelivery,
			documents: [],
			orders_sale_code: [Number(item.orders_sale_id)],
		};

		dispatch({
			type: SELECT_ORDER_DATA,
			payload: {
				order: newOrder,
			},
		});

		navigate("/guias_despacho/ingresar");
	};

	const handleDelete = () => {
		setShowDelete(false);

		const request = {
			base,
			element: item,
		};

		remove(request, {
			onSuccess: (_response) => {
				addToast(`Factura eliminada correctamente.`, {
					appearance: "success",
				});
			},
		});
	};

	const handlePrint = () => {
		const allIds = data[indice]?.items.flatMap((item) => item.inventories.flatMap((inventory) => inventory.ids.split(",").map((id) => Number(id.trim()))));

		print(allIds, {
			onSuccess: (response) => {
				const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

				printJS(blobUrl);
			},
		});
	};

	const handleViewDocument = () => {
		if (item.documents && item.documents.length > 0) {
			window.open(item.documents[0].url, "_blank");
		}
	};

	const items = [];

	if (Number(item.reception) !== 100 && permissions_delivery) {
		items.push(
			{
				label: "Recepcionar",
				action: () => setShowReceive(true),
				icon: Inbox,
			},
			{
				label: `Generar ${textConstants[customer.country_id].delivery}`,
				action: () => setShowDelivery(true),
				icon: Truck,
			},
		);
	}

	if (hasPermission("edit", permissions)) {
		items.push({
			label: "Editar",
			action: handleEdit,
			icon: Edit,
		});
	}

	if (item.documents && item.documents.length > 0) {
		items.push({
			label: "Ver Documento",
			action: handleViewDocument,
			icon: FileText,
		});
	}

	items.push({
		label: "Imprimir Etiquetas",
		action: handlePrint,
		icon: QrCode,
	});

	if (hasPermission("delete", permissions)) {
		items.push({
			label: "Eliminar",
			action: () => setShowDelete(true),
			icon: Trash,
			danger: true,
		});
	}

	return (
		<>
			<CustomDropdown title="Acciones" items={items} align="right" isLoading={loadDelete || loadReceive || loadAssign || loadPrint} />

			<BasicConfirm
				subMessage={
					"Eliminar la factura retirará permanentemente las guías de despacho asociadas, los productos en inventario y sus aplicaciones registradas."
				}
				message={"¿Está seguro de que desea eliminar la factura seleccionada?"}
				showConfirm={showDelete}
				setShowConfirm={setShowDelete}
				handleAction={handleDelete}
				showValidation={true}
				textValidation={"eliminar la factura seleccionada"}
			/>

			<BasicConfirm
				message={"¿Está seguro de que desea recepcionar completamente la factura seleccionada?"}
				showConfirm={showReceive}
				setShowConfirm={setShowReceive}
				handleAction={handleReceive}
				showValidation={true}
				subMessage={"Al confirmar, se generarán las etiquetas de los productos para su impresión."}
				textValidation={"recepcionar la factura seleccionada"}
			/>

			<BasicConfirm
				message={`¿Está seguro de que desea generar una ${textConstants[customer.country_id].delivery} de la factura seleccionada?`}
				showConfirm={showDelivery}
				setShowConfirm={setShowDelivery}
				handleAction={handleDelivery}
				showValidation={true}
				textValidation={`generar ${textConstants[customer.country_id].delivery}`}
			/>

			<OrdersSaleAssign showAssign={showAssign} setShowAssign={setShowAssign} item={item} base={base} assign={assign} />
		</>
	);
};

export default OrdersSaleActionItem;
